import type {
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";
import { cn } from "@/lib/cn";

type Align = "left" | "center" | "right";

const alignClasses: Record<Align, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function DataTableShell({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded border border-rule bg-surface shadow-sm",
        className,
      )}
      {...props}
    />
  );
}

export function DataTableScroll({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("overflow-x-auto", className)} {...props} />;
}

export function DataTable({
  responsive = false,
  className,
  ...props
}: TableHTMLAttributes<HTMLTableElement> & { responsive?: boolean }) {
  return (
    <table
      className={cn(
        "w-full border-collapse text-left text-xs",
        responsive && "block md:table",
        className,
      )}
      {...props}
    />
  );
}

export function DataTableHead({
  responsive = false,
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement> & { responsive?: boolean }) {
  return (
    <thead
      className={cn(
        "border-b border-rule bg-elevated font-display text-[0.6rem] uppercase tracking-wider text-ink-muted",
        responsive && "hidden md:table-header-group",
        className,
      )}
      {...props}
    />
  );
}

export function DataTableBody({
  responsive = false,
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement> & { responsive?: boolean }) {
  return (
    <tbody
      className={cn("divide-y divide-rule/60", responsive && "block md:table-row-group", className)}
      {...props}
    />
  );
}

export function DataTableHeaderRow({
  className,
  ...props
}: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("border-b border-rule", className)} {...props} />;
}

export function DataTableRow({
  interactive = true,
  className,
  ...props
}: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr
      className={cn(
        "border-b border-rule/60 last:border-b-0",
        interactive && "transition-colors hover:bg-field",
        className,
      )}
      {...props}
    />
  );
}

export function DataTableHeadCell({
  align = "left",
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return (
    <th
      className={cn("px-3 py-2 font-normal", alignClasses[align], className)}
      {...props}
    />
  );
}

export function DataTableCell({
  align = "left",
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return (
    <td
      className={cn("px-3 py-2 align-middle", alignClasses[align], className)}
      {...props}
    />
  );
}

export function DataTableEmptyRow({
  colSpan,
  children,
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & {
  colSpan: number;
  children: ReactNode;
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className={cn("px-3 py-6 text-center text-ink-muted", className)}
        {...props}
      >
        {children}
      </td>
    </tr>
  );
}

export function DataTablePagination({
  first,
  last,
  total,
  page,
  totalPages,
  onPrevious,
  onNext,
  summaryTestId,
  previousTestId,
  indicatorTestId,
  nextTestId,
  className,
}: {
  first: number;
  last: number;
  total: number;
  page: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  summaryTestId?: string;
  previousTestId?: string;
  indicatorTestId?: string;
  nextTestId?: string;
  className?: string;
}) {
  return (
    <footer
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 text-xs text-ink-muted",
        className,
      )}
    >
      <span data-testid={summaryTestId} className="font-mono">
        {first}-{last} of {total}
      </span>
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={onPrevious}
          disabled={page <= 1}
          data-testid={previousTestId}
          className="rounded border border-rule px-3 py-1.5 font-display uppercase tracking-wider text-ink-muted transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40"
        >
          Prev
        </button>
        <span
          data-testid={indicatorTestId}
          className="min-w-20 text-center font-mono"
        >
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages}
          data-testid={nextTestId}
          className="rounded border border-rule px-3 py-1.5 font-display uppercase tracking-wider text-ink-muted transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </footer>
  );
}
